import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { trackEvent } from "@/hooks/useTracking";
import { useTrackVisibility } from "@/hooks/useTrackVisibility";
import { useRtdProgress } from "@/hooks/use-rtd-progress";
import { useEngagementTime } from "@/hooks/useEngagementTime";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { formatTimeAgo } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Grid3X3,
  Star,
  Shield,
  Eye,
  Phone,
  MapPin,
  MousePointer,
  ArrowUpRight,
  Zap,
  Flame,
  Bell,
  Info,
  Lightbulb,
  Clock,
  DollarSign,
  TrendingUp,
  Pencil,
  ChevronRight,
  CheckCircle2,
  Circle,
  MessageSquare,
  BarChart3,
  Search,
  Rocket,
  Sparkles,
  CalendarDays,
  Bot,
  Target,
  RefreshCw,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, Review, ListingChange, GeogridScan, GbpSnapshot, Notification, Streak, Account, TokenEvent, Keyword, GridPoint } from "@shared/schema";
import { Link } from "wouter";
import { GoogleMap, useJsApiLoader, OverlayViewF, OverlayView } from "@react-google-maps/api";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useRecommendations } from "@/hooks/use-recommendations";
import { getRecommendationIcon } from "@/lib/recommendation-icons";

function CoverageScoreCard({ scans }: { scans: GeogridScan[] }) {
  const latestScan = scans[0];

  const computeScore = (gridData: number[][]) => {
    const flat = gridData.flat().filter(r => r > 0);
    if (flat.length === 0) return 0;
    const top3 = flat.filter(r => r <= 3).length;
    return Math.round((top3 / flat.length) * 100);
  };

  const score = latestScan ? computeScore(latestScan.gridData as number[][]) : 0;
  const prevScore = scans[1] ? computeScore(scans[1].gridData as number[][]) : null;
  const delta = prevScore !== null ? score - prevScore : null;

  return (
    <Card className="border-brand-blue/20 bg-gradient-to-br from-card via-card to-brand-blue/5">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Coverage Score</p>
            {latestScan ? (
              <div className="flex items-end gap-2">
                <p className="text-3xl font-bold tracking-tight" data-testid="text-coverage-score">{score}%</p>
                {delta !== null && delta !== 0 && (
                  <Badge variant="outline" className={`text-[10px] mb-1 ${delta > 0 ? "text-emerald-500 border-emerald-500/30" : "text-red-500 border-red-500/30"}`}>
                    {delta > 0 ? "+" : ""}{delta}%
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-2xl font-bold text-muted-foreground" data-testid="text-coverage-score">--</p>
            )}
            <p className="text-[11px] text-muted-foreground">top-3 rank positions</p>
          </div>
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand-blue/10">
            <Target className="w-5 h-5 text-brand-blue" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueHeroCard({
  location,
  snapshot,
  loading,
  onEditAssumptions,
}: {
  location: Location | undefined;
  snapshot: GbpSnapshot | null | undefined;
  loading: boolean;
  onEditAssumptions: () => void;
}) {
  const aov = parseFloat(String(location?.aov || "0")) || 0;
  const bookRateVal = parseFloat(String(location?.bookRate || "0")) || 0;
  const bookRate = bookRateVal > 1 ? bookRateVal / 100 : bookRateVal;
  const showRateVal = parseFloat(String(location?.showRate || "0")) || 0;
  const showRate = showRateVal > 1 ? showRateVal / 100 : showRateVal;
  const closeRateVal = parseFloat(String(location?.closeRate || "0")) || 0;
  const closeRate = closeRateVal > 1 ? closeRateVal / 100 : closeRateVal;

  const incomingCalls = snapshot?.calls ?? 0;
  const totalReach = (snapshot?.views ?? 0) + incomingCalls + (snapshot?.directions ?? 0) + (snapshot?.clicks ?? 0);
  const booked = Math.round(incomingCalls * (bookRate || 0.6));
  const ran = Math.round(booked * (showRate || 0.4));
  const closed = Math.round(ran * (closeRate || 0.3));
  const estimatedRevenue = closed * (aov || 250);

  return (
    <Card className="border-brand-orange/20 bg-gradient-to-br from-card via-card to-brand-orange/5">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <h2 className="text-lg font-bold" data-testid="text-hero-title">Revenue Hero</h2>
              <p className="text-xs text-muted-foreground">Estimated impact from your Google presence</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onEditAssumptions} data-testid="button-edit-assumptions">
            <Pencil className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
              <p className="text-xs text-muted-foreground mb-1">Incoming Calls</p>
              <p className="text-2xl font-bold" data-testid="text-hero-reach">{incomingCalls.toLocaleString()}</p>
              <p className="text-[11px] text-muted-foreground">from Google profile</p>
            </div>
            <div className="p-4 rounded-xl bg-brand-blue/5 border border-brand-blue/20">
              <p className="text-xs text-muted-foreground mb-1">Closed Deals</p>
              <p className="text-2xl font-bold text-brand-blue" data-testid="text-hero-opportunities">
                {closed.toLocaleString()}
              </p>
              <p className="text-[11px] text-muted-foreground">{incomingCalls} → {booked} → {ran} → {closed}</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <p className="text-xs text-muted-foreground mb-1">Est. Mo. Revenue</p>
              <p className="text-2xl font-bold text-emerald-500" data-testid="text-hero-revenue">
                ${estimatedRevenue.toLocaleString()}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {aov > 0 ? `${closed} closed × $${aov} AOV` : "Set AOV to calculate"}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelGraphic({ stages }: { stages: { label: string; pct: number }[] }) {
  const colors = [
    "rgba(246, 100, 42, 0.85)",
    "rgba(246, 100, 42, 0.65)",
    "rgba(246, 100, 42, 0.45)",
    "rgba(246, 100, 42, 0.30)",
  ];
  const totalHeight = 280;
  const stageHeight = totalHeight / stages.length;
  const maxWidth = 260;
  const minWidth = 60;

  return (
    <div className="flex items-start justify-center gap-6 py-2">
      <div className="relative" style={{ width: maxWidth + 40, height: totalHeight }}>
        {stages.map((stage, i) => {
          const topWidth = maxWidth - ((maxWidth - minWidth) / stages.length) * i;
          const bottomWidth = maxWidth - ((maxWidth - minWidth) / stages.length) * (i + 1);
          const topLeft = (maxWidth - topWidth) / 2 + 20;
          const topRight = topLeft + topWidth;
          const bottomLeft = (maxWidth - bottomWidth) / 2 + 20;
          const bottomRight = bottomLeft + bottomWidth;
          const yTop = i * stageHeight;
          const yBottom = yTop + stageHeight - 4;

          return (
            <svg
              key={stage.label}
              className="absolute left-0"
              style={{ top: yTop }}
              width={maxWidth + 40}
              height={stageHeight}
              viewBox={`0 ${yTop} ${maxWidth + 40} ${stageHeight}`}
            >
              <polygon
                points={`${topLeft},${yTop} ${topRight},${yTop} ${bottomRight},${yBottom} ${bottomLeft},${yBottom}`}
                fill={colors[i]}
                rx="4"
              />
              <text
                x={(maxWidth + 40) / 2}
                y={yTop + stageHeight / 2 + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-white font-semibold"
                fontSize="11"
              >
                {stage.label}
              </text>
            </svg>
          );
        })}
      </div>
      <div className="flex flex-col justify-around" style={{ height: totalHeight }}>
        {stages.map((stage) => (
          <div key={stage.label} className="flex items-center gap-2">
            <div className="w-8 border-t border-dashed border-muted-foreground/40" />
            <span className="text-sm font-bold text-foreground whitespace-nowrap">{stage.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditAssumptionsModal({
  open,
  onClose,
  location,
}: {
  open: boolean;
  onClose: () => void;
  location: Location | undefined;
}) {
  const [aov, setAov] = useState("");
  const [closeRate, setCloseRate] = useState("");
  const [bookRate, setBookRate] = useState("");
  const [showRate, setShowRate] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setAov(String(location?.aov || ""));
      setCloseRate(String(location?.closeRate || ""));
      setBookRate(String(location?.bookRate || ""));
      setShowRate(String(location?.showRate || ""));
    }
  }, [open, location?.aov, location?.closeRate, location?.bookRate, location?.showRate]);

  const bookPct = parseFloat(bookRate) || 60;
  const showPct = parseFloat(showRate) || 40;
  const closePct = parseFloat(closeRate) || 30;

  const funnelStages = [
    { label: "Incoming Calls", pct: 100 },
    { label: "Booked", pct: bookPct },
    { label: "Ran", pct: showPct },
    { label: "Closed", pct: closePct },
  ];

  const mutation = useMutation({
    mutationFn: async () => {
      if (!location) return;
      await apiRequest("PATCH", `/api/locations/${location.id}`, {
        aov: aov || undefined,
        closeRate: closeRate || undefined,
        bookRate: bookRate || undefined,
        showRate: showRate || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Assumptions updated", description: "Revenue funnel has been recalculated." });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: "Update failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Revenue Hero — Conversion Funnel</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <FunnelGraphic stages={funnelStages} />

          <p className="text-xs text-muted-foreground text-center italic">Industry average for companies with good operating procedures is 60-40-30.</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Booked Rate (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="60"
                  value={bookRate}
                  onChange={(e) => setBookRate(e.target.value)}
                  className="pr-8 h-9"
                  data-testid="input-edit-book-rate"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Ran / Show Rate (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="40"
                  value={showRate}
                  onChange={(e) => setShowRate(e.target.value)}
                  className="pr-8 h-9"
                  data-testid="input-edit-show-rate"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Close Rate (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="30"
                  value={closeRate}
                  onChange={(e) => setCloseRate(e.target.value)}
                  className="pr-8 h-9"
                  data-testid="input-edit-close-rate"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Avg Order Value ($)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="e.g. 250"
                  value={aov}
                  onChange={(e) => setAov(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="input-edit-aov"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              className="flex-1 bg-brand-orange hover:bg-brand-orange/90"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              data-testid="button-save-assumptions"
            >
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuickActionsBar() {
  // "Ask Aria" doesn't navigate — Aria is a floating chat widget (no /assistant
  // route). It dispatches a global "aria:open" event that AriaChat listens for.
  const actions: Array<{
    label: string;
    icon: typeof Grid3X3;
    color: string;
    bg: string;
    href?: string;
    onClick?: () => void;
  }> = [
    { label: "Run Scan", icon: Grid3X3, href: "/geogrid", color: "text-brand-blue", bg: "bg-brand-blue/10" },
    { label: "Schedule Post", icon: CalendarDays, href: "/posts", color: "text-brand-orange", bg: "bg-brand-orange/10" },
    { label: "View Reviews", icon: Star, href: "/reviews", color: "text-amber-500", bg: "bg-amber-500/10" },
    { label: "Ask Aria", icon: Sparkles, onClick: () => window.dispatchEvent(new Event("aria:open")), color: "text-violet-500", bg: "bg-violet-500/10" },
    { label: "Automation", icon: Bot, href: "/automation", color: "text-emerald-500", bg: "bg-emerald-500/10" },
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {actions.map((action) => {
        const button = (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 shrink-0 hover:bg-muted/50"
            data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={action.onClick}
          >
            <div className={`w-6 h-6 rounded-md ${action.bg} flex items-center justify-center`}>
              <action.icon className={`w-3.5 h-3.5 ${action.color}`} />
            </div>
            <span className="text-xs font-medium">{action.label}</span>
          </Button>
        );
        return action.href ? (
          <Link key={action.label} href={action.href}>{button}</Link>
        ) : (
          <div key={action.label}>{button}</div>
        );
      })}
    </div>
  );
}

function NextBestActionCard() {
  const { recommendations } = useRecommendations();
  const top = recommendations[0];

  if (!top) return null;

  const priorityStyles: Record<string, { color: string; bgColor: string }> = {
    high: { color: "text-red-500", bgColor: "bg-red-500/10" },
    medium: { color: "text-amber-500", bgColor: "bg-amber-500/10" },
    low: { color: "text-brand-blue", bgColor: "bg-brand-blue/10" },
  };

  const style = priorityStyles[top.priority] || priorityStyles.medium;

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <p className="text-sm font-semibold">Next Best Action</p>
        </div>
        <Link href={top.page}>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer" data-testid="card-next-action">
            <div className={`w-12 h-12 rounded-xl ${style.bgColor} flex items-center justify-center shrink-0`}>
              {getRecommendationIcon(top.iconName, `w-6 h-6 ${style.color}`)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{top.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{top.description}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

const RTD_LEVELS = [
  { id: 1, name: "Foundation", color: "#e8732a", tasks: ["geogrid", "category", "description", "services", "service_desc"] },
  { id: 2, name: "Profile Strength", color: "#4ecdc4", tasks: ["essentials", "reviews", "images"] },
  { id: 3, name: "Content Engine", color: "#a78bfa", tasks: ["posts", "publish_post", "schedule_post", "products", "publish_product"] },
  { id: 4, name: "Expand", color: "#ff8c00", tasks: ["social_sync", "missing_profiles"] },
];

const RTD_TASK_LABELS: Record<string, { label: string; route: string }> = {
  geogrid: { label: "Run a GeoGrid Scan", route: "/geogrid" },
  category: { label: "Category Optimization", route: "/gbp-optimization" },
  description: { label: "Business Description", route: "/gbp-optimization" },
  services: { label: "Add Services", route: "/gbp-optimization" },
  service_desc: { label: "Add Service Descriptions", route: "/gbp-optimization" },
  essentials: { label: "Complete Profile Essentials", route: "/profile-health" },
  reviews: { label: "Reply to All Reviews", route: "/reviews" },
  images: { label: "Add Images", route: "/media" },
  posts: { label: "Posts Engine", route: "/posts" },
  publish_post: { label: "Publish a Post Now", route: "/posts" },
  schedule_post: { label: "Schedule a Post", route: "/posts" },
  products: { label: "Product Forge", route: "/products" },
  publish_product: { label: "Publish a Product to GBP", route: "/products" },
  social_sync: { label: "Sync Social Fortress", route: "/social" },
  missing_profiles: { label: "Add Missing Socials", route: "/social" },
};

function RtdProgressCard() {
  const { completedTasks, currentLevel } = useRtdProgress();
  const totalTasks = 15;
  const progress = (currentLevel / totalTasks) * 100;
  const allDone = currentLevel >= totalTasks;

  const nextTasks: { id: string; label: string; route: string; levelName: string; levelColor: string }[] = [];
  if (!allDone) {
    for (const level of RTD_LEVELS) {
      for (const taskId of level.tasks) {
        if (!completedTasks.has(taskId) && nextTasks.length < 3) {
          const info = RTD_TASK_LABELS[taskId];
          if (info) nextTasks.push({ id: taskId, label: info.label, route: info.route, levelName: level.name, levelColor: level.color });
        }
      }
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Rocket className="w-4 h-4 text-brand-orange" />
            Road to Domination
          </CardTitle>
          <span className="text-xs font-semibold" style={{ color: "#ff8c00" }}>LVL {currentLevel}/{totalTasks}</span>
        </div>
        <Progress value={progress} className="h-1.5 mt-2" data-testid="progress-rtd" />
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1.5">
          {RTD_LEVELS.map(level => {
            const done = level.tasks.every(t => completedTasks.has(t));
            const partial = level.tasks.some(t => completedTasks.has(t));
            const levelDone = level.tasks.filter(t => completedTasks.has(t)).length;
            return (
              <div key={level.id} className="flex-1 text-center" data-testid={`rtd-level-${level.id}`}>
                <div
                  className="h-1.5 rounded-full mb-1"
                  style={{
                    background: done ? level.color : partial ? `linear-gradient(90deg, ${level.color} ${(levelDone / level.tasks.length) * 100}%, rgba(255,255,255,0.1) 0%)` : "rgba(255,255,255,0.08)",
                  }}
                />
                <span className="text-[10px]" style={{ color: done ? level.color : partial ? level.color + "99" : "#ffffff30" }}>
                  {done ? "\u2713 " : ""}{level.name}
                </span>
              </div>
            );
          })}
        </div>

        {allDone ? (
          <div className="text-center py-3">
            <div className="text-lg font-bold" style={{ color: "#00D4AA" }}>Domination Achieved!</div>
            <p className="text-xs text-muted-foreground mt-1">All tasks complete. Your profile is fully optimized.</p>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider">Next Steps</p>
            {nextTasks.map(task => (
              <Link key={task.id} href={task.route}>
                <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`rtd-next-${task.id}`}>
                  <Circle className="w-4 h-4 shrink-0" style={{ color: task.levelColor }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{task.label}</p>
                    <p className="text-[10px]" style={{ color: task.levelColor }}>{task.levelName}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link href="/road-to-domination">
          <div className="text-center pt-1">
            <span className="text-xs text-brand-orange hover:underline cursor-pointer" data-testid="link-rtd-full">View Full Progress →</span>
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

function KpiCard({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBg,
  loading,
}: {
  title: string;
  value: string | null;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-1">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-7 w-16" />
                <p className="text-[11px] text-muted-foreground/60">Data syncing...</p>
              </div>
            ) : (
              <p className="text-2xl font-bold tracking-tight" data-testid={`text-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                {value ?? "--"}
              </p>
            )}
          </div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-xl ${iconBg}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TokenWalletCard({ balance, recentEvents }: { balance: number; recentEvents: TokenEvent[] }) {
  return (
    <Card className="border-brand-orange/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-brand-orange" />
            Token Wallet
          </CardTitle>
          <Link href="/leaderboard">
            <Button variant="ghost" size="sm" className="text-xs" data-testid="link-view-leaderboard">
              Leaderboard <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="p-4 rounded-xl bg-gradient-to-r from-brand-orange/10 to-brand-orange/5 border border-brand-orange/20 text-center">
          <p className="text-3xl font-bold text-brand-orange" data-testid="text-wallet-balance">{balance.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">tokens earned</p>
        </div>
        {recentEvents.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">Recent Activity</p>
            {recentEvents.slice(0, 4).map((event) => (
              <div key={event.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/20" data-testid={`token-event-${event.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${event.amount > 0 ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                    <Zap className={`w-3 h-3 ${event.amount > 0 ? "text-emerald-500" : "text-red-500"}`} />
                  </div>
                  <span className="text-xs truncate">{event.eventType.replace(/_/g, " ")}</span>
                </div>
                <span className={`text-xs font-semibold shrink-0 ${event.amount > 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {event.amount > 0 ? "+" : ""}{event.amount}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityFeed({ notifications }: { notifications: Notification[] }) {
  function getIcon(type: string) {
    switch (type) {
      case "welcome": return Bell;
      case "tip": return Lightbulb;
      case "review": return Star;
      case "listing": return Shield;
      default: return Info;
    }
  }

  const timeAgo = formatTimeAgo;

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-1">
        <CardTitle className="text-sm font-semibold">Activity Feed</CardTitle>
        <Link href="/notifications">
          <Button variant="ghost" size="sm" data-testid="link-view-notifications">
            View All <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-2">
        {notifications.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Your activity will appear here once your profile is live.
            </p>
          </div>
        ) : (
          notifications.map((n) => {
            const Icon = getIcon(n.type);
            return (
              <div key={n.id} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30" data-testid={`feed-item-${n.id}`}>
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {timeAgo(n.createdAt)}
                </span>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function StreakWidget({ streakList }: { streakList: Streak[] }) {
  if (streakList.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          Streaks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {streakList.map((s) => (
          <div key={s.id} className="flex items-center justify-between" data-testid={`streak-${s.streakType}`}>
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-sm capitalize">{s.streakType.replace(/_/g, " ")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold">{s.currentCount}</span>
              <span className="text-xs text-muted-foreground">days</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RecentReviewsCard({ reviews }: { reviews: Review[] }) {
  const recentReviews = reviews.slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-1">
        <CardTitle className="text-sm font-semibold">Recent Reviews</CardTitle>
        <Link href="/reviews">
          <Button variant="ghost" size="sm" data-testid="link-view-all-reviews">
            View All <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {recentReviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reviews yet.</p>
        ) : (
          recentReviews.map((review) => (
            <div key={review.id} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30" data-testid={`card-review-${review.id}`}>
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 shrink-0">
                <span className="text-xs font-semibold text-primary">
                  {review.reviewerName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-sm font-medium truncate">{review.reviewerName}</span>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3 h-3 ${
                          i < review.rating
                            ? "fill-amber-400 text-amber-400"
                            : "text-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{review.text}</p>
                {!review.reply && (
                  <Badge variant="outline" className="mt-2 text-xs">Needs Reply</Badge>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function ListingHealthCard({ changes }: { changes: ListingChange[] }) {
  const unresolvedChanges = changes.filter((c) => !c.isReverted);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-1">
        <CardTitle className="text-sm font-semibold">Listing Health</CardTitle>
        <Link href="/listing">
          <Button variant="ghost" size="sm" data-testid="link-view-listing">
            View All <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {unresolvedChanges.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <Shield className="w-6 h-6 text-emerald-500" />
            </div>
            <p className="text-sm font-medium">All Clear</p>
            <p className="text-xs text-muted-foreground mt-1">No unauthorized changes detected</p>
          </div>
        ) : (
          <div className="space-y-2">
            {unresolvedChanges.slice(0, 3).map((change) => (
              <div
                key={change.id}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  change.severity === "high"
                    ? "bg-red-50 dark:bg-red-950/20"
                    : change.severity === "medium"
                    ? "bg-amber-50 dark:bg-amber-950/20"
                    : "bg-muted/30"
                }`}
                data-testid={`card-change-${change.id}`}
              >
                <Shield
                  className={`w-4 h-4 shrink-0 ${
                    change.severity === "high"
                      ? "text-red-500"
                      : change.severity === "medium"
                      ? "text-amber-500"
                      : "text-muted-foreground"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{change.field} changed</p>
                  <p className="text-xs text-muted-foreground truncate">
                    &quot;{change.oldValue}&quot; to &quot;{change.newValue}&quot;
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getRankColorDash(rank: number): string {
  if (rank <= 0 || rank > 20) return "#dc2626";
  if (rank === 1) return "#166534";
  if (rank <= 3) return "#22c55e";
  if (rank <= 10) return "#eab308";
  return "#f97316";
}

function getRankLabelDash(rank: number): string {
  if (rank <= 0 || rank > 20) return "20+";
  return String(rank);
}

function DashboardMapPreview({ scan, points, apiKey }: { scan: GeogridScan; points: GridPoint[]; apiKey: string }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: "google-maps-script",
  });

  const [mapsAuthFailed, setMapsAuthFailed] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // A failed Maps key auth (referrer-restricted, disabled, billing off, API not
  // enabled) leaves the loader promise hanging — isLoaded stays false and
  // loadError stays undefined. Catch the global gm_authFailure callback plus a
  // hard timeout so the preview hides instead of showing an endless skeleton.
  useEffect(() => {
    const prev = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => setMapsAuthFailed(true);
    return () => {
      (window as any).gm_authFailure = prev;
    };
  }, []);

  useEffect(() => {
    if (isLoaded || loadError) return;
    const t = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(t);
  }, [isLoaded, loadError]);

  const markers = useMemo(() => {
    return points.map((pt, i) => ({
      lat: parseFloat(pt.lat),
      lng: parseFloat(pt.lng),
      rank: pt.rank != null ? pt.rank : 21,
      index: i,
    })).filter(m => !isNaN(m.lat) && !isNaN(m.lng));
  }, [points]);

  const centerLat = scan.centerLat ? parseFloat(scan.centerLat) : markers[0]?.lat || 0;
  const centerLng = scan.centerLng ? parseFloat(scan.centerLng) : markers[0]?.lng || 0;

  const onMapLoad = (map: google.maps.Map) => {
    if (markers.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, { top: 10, right: 10, bottom: 10, left: 10 });
  };

  if (loadError || mapsAuthFailed || (loadTimedOut && !isLoaded)) return null;

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center" style={{ height: 220 }}>
        <Skeleton className="w-full h-full rounded-lg" />
      </div>
    );
  }

  if (markers.length === 0) return null;

  const markerSize = scan.gridSize >= 11 ? 22 : scan.gridSize >= 7 ? 28 : 32;
  const fontSize = scan.gridSize >= 11 ? 8 : scan.gridSize >= 7 ? 10 : 12;

  return (
    <div className="rounded-lg overflow-hidden border border-border" style={{ height: 220 }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat: centerLat, lng: centerLng }}
        zoom={10}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: "none",
          clickableIcons: false,
        }}
      >
        {markers.map((m) => (
          <OverlayViewF
            key={m.index}
            position={{ lat: m.lat, lng: m.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={{ transform: "translate(-50%, -50%)" }}>
              <div
                className="rounded-full flex items-center justify-center text-white font-bold border border-white/40"
                style={{
                  backgroundColor: getRankColorDash(m.rank),
                  width: markerSize,
                  height: markerSize,
                  fontSize,
                  lineHeight: 1,
                }}
              >
                {getRankLabelDash(m.rank)}
              </div>
            </div>
          </OverlayViewF>
        ))}
      </GoogleMap>
    </div>
  );
}

function GeoGridPreviewCard({ scans, keywords }: { scans: GeogridScan[]; keywords: Keyword[] }) {
  const completedScans = scans.filter(s => s.status === "completed" && ((s.gridData as number[][])?.length || 0) > 0);
  const latestScan = completedScans[0];
  const keyword = latestScan ? keywords.find(k => k.id === latestScan.keywordId) : null;

  const { data: mapsConfig } = useQuery<{ apiKey: string | null }>({
    queryKey: ["/api/config/maps-key"],
  });

  const { data: scanDetail } = useQuery<GeogridScan & { gridPoints: GridPoint[] }>({
    queryKey: ["/api/geogrid-scans/scan", latestScan?.id],
    enabled: !!latestScan?.id && !!mapsConfig?.apiKey,
  });

  const gridPoints = scanDetail?.gridPoints || [];

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-1">
        <CardTitle className="text-sm font-semibold">Latest GeoGrid Scan</CardTitle>
        <Link href="/geogrid">
          <Button variant="ghost" size="sm" data-testid="link-view-geogrid">
            View Details <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {latestScan ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium" data-testid="text-latest-keyword">{keyword?.keyword || "Unknown Keyword"}</p>
                <p className="text-[10px] text-muted-foreground">
                  {latestScan.gridSize}x{latestScan.gridSize} · {latestScan.radius}mi · {latestScan.scannedAt ? new Date(latestScan.scannedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                </p>
              </div>
              <div className="flex gap-3 text-center">
                <div>
                  <p className="text-lg font-bold font-mono text-amber-400" data-testid="text-dash-avg-rank">{latestScan.avgRank ? Number(latestScan.avgRank).toFixed(1) : "--"}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">AGR</p>
                </div>
                <div>
                  <p className={`text-lg font-bold font-mono ${latestScan.solvScore && parseFloat(latestScan.solvScore) >= 50 ? "text-emerald-400" : "text-red-400"}`} data-testid="text-dash-solv">{latestScan.solvScore ? `${parseFloat(latestScan.solvScore).toFixed(1)}%` : "--"}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">SoLV</p>
                </div>
              </div>
            </div>
            {mapsConfig?.apiKey && gridPoints.length > 0 ? (
              <DashboardMapPreview scan={latestScan} points={gridPoints} apiKey={mapsConfig.apiKey} />
            ) : (
              <div className="flex items-center justify-center" style={{ height: 220 }}>
                <Skeleton className="w-full h-full rounded-lg" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Grid3X3 className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm font-medium">No Scans Yet</p>
            <p className="text-xs text-muted-foreground mt-1">Run your first GeoGrid scan</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { selectedLocationId } = useLocationStore();
  const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);

  const { data: locations, isLoading: locLoading } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: snapshot, isLoading: snapshotLoading } = useQuery<GbpSnapshot | null>({
    queryKey: ["/api/dashboard/snapshot", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: scopeCheck } = useQuery<{ hasGbpScope: boolean }>({
    queryKey: ["/api/gbp/scope-check"],
    enabled: !!locationId,
  });

  const { data: tokenData } = useQuery<{ balance: number }>({
    queryKey: ["/api/dashboard/tokens", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: tokenEvents = [] } = useQuery<TokenEvent[]>({
    queryKey: ["/api/token-events", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: feedItems = [] } = useQuery<Notification[]>({
    queryKey: ["/api/dashboard/feed", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: streakList = [] } = useQuery<Streak[]>({
    queryKey: ["/api/dashboard/streaks", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: reviews = [] } = useQuery<Review[]>({
    queryKey: ["/api/reviews", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: changes = [] } = useQuery<ListingChange[]>({
    queryKey: ["/api/listing-changes", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: scans = [] } = useQuery<GeogridScan[]>({
    queryKey: ["/api/geogrid-scans", locationId],
    enabled: !!locationId,
    placeholderData: keepPreviousData,
  });

  const { data: account } = useQuery<Account>({
    queryKey: ["/api/account"],
    enabled: !!locationId,
  });

  const { data: keywords = [] } = useQuery<Keyword[]>({
    queryKey: ["/api/keywords", locationId],
    enabled: !!locationId,
  });

  const { toast } = useToast();

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/gbp/sync/${locationId}`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/snapshot", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/reviews", locationId] });
      if (data.needsGbpScope) {
        toast({
          title: "Google permissions needed",
          description: "Go to Settings and click 'Connect Google Business Profile' to see your performance data.",
          variant: "destructive",
        });
      } else if (data.success === false && data.message) {
        toast({ title: "Sync issue", description: data.message, variant: "destructive" });
      } else if (data.errors) {
        toast({
          title: "Partial sync",
          description: "Some data couldn't be fetched. Try reconnecting your Google account in Settings.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Google sync complete",
          description: data.newReviews > 0
            ? `${data.newReviews} new review${data.newReviews > 1 ? "s" : ""} imported`
            : "Data is up to date",
        });
      }
    },
    onError: () => {
      toast({ title: "Sync failed", description: "Could not sync with Google. Try reconnecting your account.", variant: "destructive" });
    },
  });

  const needsGbpScope = scopeCheck && !scopeCheck.hasGbpScope;

  const currentLocation = locations?.find((l) => l.id === locationId);
  const autoSyncTriggered = useRef<string | null>(null);
  useEffect(() => {
    if (!locationId || locationId === autoSyncTriggered.current || snapshotLoading || syncMutation.isPending || needsGbpScope) return;
    const hasGbp = currentLocation?.gbpLocationId;
    if (!hasGbp) return;
    const isStale = snapshot?.snappedAt
      ? (Date.now() - new Date(snapshot.snappedAt).getTime()) > 6 * 60 * 60 * 1000
      : true;
    if (!snapshot || isStale) {
      autoSyncTriggered.current = locationId;
      syncMutation.mutate();
    }
  }, [locationId, snapshotLoading, snapshot, needsGbpScope, currentLocation]);
  const hasSnapshot = !!snapshot && !snapshotLoading;
  const noSnapshotData = !snapshotLoading && !snapshot;
  const showKpiLoading = snapshotLoading || syncMutation.isPending;

  if (locLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-dashboard-title">
            {currentLocation?.name || "Dashboard"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {currentLocation?.city ? `${currentLocation.city}` : "Overview for your business"}
            {snapshot?.snappedAt && (
              <> · Last updated {new Date(snapshot.snappedAt).toLocaleDateString()}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { trackEvent('dashboard_sync_clicked'); syncMutation.mutate(); }}
            disabled={syncMutation.isPending}
            data-testid="button-sync-google"
          >
            {syncMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {syncMutation.isPending ? "Syncing..." : "Sync Google"}
          </Button>
          <Link href="/leaderboard">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-orange/5 border border-brand-orange/10 hover:bg-brand-orange/10 transition-colors cursor-pointer" data-testid="badge-token-balance">
              <Zap className="w-4 h-4 text-brand-orange" />
              <span className="text-sm font-semibold" data-testid="text-token-balance">
                {tokenData?.balance ?? 0}
              </span>
              <span className="text-xs text-muted-foreground">tokens</span>
            </div>
          </Link>
          {scans.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 border border-border/50">
              <Search className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold" data-testid="text-scan-count">{scans.length}</span>
              <span className="text-xs text-muted-foreground">scans</span>
            </div>
          )}
        </div>
      </div>

      <QuickActionsBar />

      <RevenueHeroCard
        location={currentLocation}
        snapshot={snapshot}
        loading={showKpiLoading}
        onEditAssumptions={() => setShowAssumptionsModal(true)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <NextBestActionCard />

          {needsGbpScope && (
            <Card className="border-amber-500/30 bg-amber-500/5" data-testid="gbp-scope-banner">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Connect Google Business Profile for metrics</p>
                    <p className="text-xs text-muted-foreground">Go to Settings and click "Connect Google Business Profile" to see your calls, views, and direction requests.</p>
                  </div>
                </div>
                <Link href="/settings">
                  <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 shrink-0" data-testid="button-connect-gbp">
                    Go to Settings
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <CoverageScoreCard scans={scans} />
            <KpiCard
              title="Calls"
              value={hasSnapshot ? String(snapshot.calls ?? 0) : "0"}
              icon={Phone}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-500/10"
              loading={showKpiLoading}
            />
            <KpiCard
              title="Profile Views"
              value={hasSnapshot ? String(snapshot.views ?? 0) : "0"}
              icon={Eye}
              iconColor="text-primary"
              iconBg="bg-primary/10"
              loading={showKpiLoading}
            />
            <KpiCard
              title="Direction Requests"
              value={hasSnapshot ? String(snapshot.directions ?? 0) : "0"}
              icon={MapPin}
              iconColor="text-amber-500"
              iconBg="bg-amber-500/10"
              loading={showKpiLoading}
            />
            <KpiCard
              title="Website Clicks"
              value={hasSnapshot ? String(snapshot.clicks ?? 0) : "0"}
              icon={MousePointer}
              iconColor="text-foreground"
              iconBg="bg-muted"
              loading={showKpiLoading}
            />
          </div>

          <ActivityFeed notifications={feedItems} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GeoGridPreviewCard scans={scans} keywords={keywords} />
            <RecentReviewsCard reviews={reviews} />
          </div>
        </div>
        <div className="space-y-4">
          <RtdProgressCard />
          <TokenWalletCard
            balance={tokenData?.balance ?? 0}
            recentEvents={tokenEvents}
          />
          <StreakWidget streakList={streakList} />
          <ListingHealthCard changes={changes} />
        </div>
      </div>

      <EditAssumptionsModal
        open={showAssumptionsModal}
        onClose={() => setShowAssumptionsModal(false)}
        location={currentLocation}
      />
    </div>
  );
}
